/**
 * Artifacts — show view on the shared Inspector grammar (coverage §2.1).
 * Header / Overview / Body / Relations / Timeline, with the three named primary
 * actions folded into the Header:
 *   - **classify**      → real `PATCH /api/artifacts/:id` (ClassifyDialog).
 *   - **advance phase** → `[PROPOSED]` (artifact_positions transition); the
 *     affordance shows a calm "ships with" toast, never a hard error.
 *   - **bind context**  → deep-links to the `context-bindings` create form
 *     (EXISTS), prefilled for this artifact.
 *
 * The bound-contexts panel (`ContextPanel`) and the artifact's merged kernel
 * timeline (`TaskTimeline`, keyed by the artifact id) render below the
 * Inspector sections.
 */
import * as React from "react";
import {
  useShow,
  useGo,
  useNavigation,
  useNotification,
  type HttpError,
} from "@refinedev/core";
import {
  Pencil,
  Tags,
  MoveRight,
  Compass,
  FolderKanban,
  UserRound,
  ExternalLink as ExternalLinkIcon,
} from "@/components/ui/icon";
import type { Artifact } from "@aos/shared";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
  FieldGrid,
  Field,
  RefLink,
  RelationsBlock,
} from "@/components/inspector";
import { Markdown } from "@/components/app";
import { humanize, formatDateTime } from "@/lib/format";
import { TaskTimeline } from "../tasks/TaskTimeline";
import { ContextPanel } from "./ContextPanel";
import { ClassifyDialog } from "./ClassifyDialog";
import { ProvenanceGraph } from "./ProvenanceGraph";

/**
 * Section-level error boundary so a thrown child degrades to a calm inline
 * notice *inside* the page instead of blanking the whole route via the app's
 * top-level boundary (frontend-followup §F: the artifacts page must never take
 * down its route). Scoped here because the artifact show composes the raw
 * Inspector primitives rather than the `ShowPage` template.
 */
class SectionBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("[ARTIFACT_SHOW_BOUNDARY]", error, info.componentStack);
  }
  override render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div
          className="max-w-5xl space-y-1 rounded-md border border-border p-4"
          role="alert"
        >
          <h2 className="text-sm font-medium text-destructive">
            This section failed to render
          </h2>
          <p className="text-sm text-muted-foreground">
            {this.state.error.message ||
              "An unexpected error occurred while rendering this artifact."}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

export function ArtifactShow() {
  const go = useGo();
  const { editUrl } = useNavigation();
  const { open } = useNotification();
  const { query } = useShow<Artifact, HttpError>({ resource: "artifacts" });
  const { data, isLoading, isError, error, refetch } = query;
  const artifact = data?.data;
  const [classifyOpen, setClassifyOpen] = React.useState(false);

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !artifact) {
    return (
      <ShowPageError
        message={error?.message}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const advancePhase = () =>
    open?.({
      type: "success",
      message: "Advance phase — coming soon",
      description:
        "Phase transitions land once POST /api/artifact-positions/:id/transition ships ([PROPOSED]).",
      key: "artifact-advance-phase",
    });

  const bindContext = () =>
    go({
      to: { resource: "context-bindings", action: "create" },
      query: { entity_type: "artifact", entity_id: artifact.id },
    });

  return (
    <>
      <SectionBoundary>
      <Inspector>
        <InspectorHeader
          title={artifact.title}
          subtitle="A tracked unit of work — its status, context bindings, and timeline."
          status={artifact.status}
          primaryAction={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>Actions</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setClassifyOpen(true)}>
                  <Tags className="mr-2 h-4 w-4" /> Classify
                </DropdownMenuItem>
                <DropdownMenuItem onClick={advancePhase}>
                  <MoveRight className="mr-2 h-4 w-4" /> Advance phase
                </DropdownMenuItem>
                <DropdownMenuItem onClick={bindContext}>
                  <Compass className="mr-2 h-4 w-4" /> Bind context
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
          actions={
            <>
              {artifact.external_url ? (
                <Button variant="outline" asChild>
                  <a
                    href={artifact.external_url}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <ExternalLinkIcon className="mr-2 h-4 w-4" /> Source
                  </a>
                </Button>
              ) : null}
              <Button
                variant="outline"
                onClick={() => go({ to: editUrl("artifacts", artifact.id) })}
              >
                <Pencil className="mr-2 h-4 w-4" /> Edit
              </Button>
            </>
          }
        />

        {/* Overview — meaningful scalar fields first (§E field prioritization).
            Foreign-key references live in the Relations block below, not here,
            so each fact appears exactly once. */}
        <InspectorSection title="Overview">
          <FieldGrid>
            <Field label="Type">{humanize(artifact.artifact_type)}</Field>
            <Field label="Status">{humanize(artifact.status)}</Field>
            <Field label="Priority">{humanize(artifact.priority)}</Field>
            <Field label="Crystallization">
              {humanize(artifact.crystallization_stage)}
            </Field>
            <Field label="Confidentiality">
              {humanize(artifact.confidentiality)}
            </Field>
            <Field label="Due date">
              {artifact.due_date
                ? new Date(artifact.due_date).toLocaleDateString()
                : null}
            </Field>
          </FieldGrid>
        </InspectorSection>

        {artifact.description && artifact.description.trim() ? (
          <InspectorSection title="Body">
            <Markdown source={artifact.description} />
          </InspectorSection>
        ) : null}

        <InspectorSection title="Relations">
          <RelationsBlock
            groups={[
              {
                title: "References",
                items: [
                  {
                    key: "project",
                    label: "Project",
                    icon: FolderKanban,
                    node: (
                      <RefLink resource="projects" id={artifact.project_id} />
                    ),
                  },
                  {
                    key: "owner",
                    label: "Owner",
                    icon: UserRound,
                    node: (
                      <RefLink resource="users" id={artifact.owner_user_id} />
                    ),
                  },
                  {
                    key: "primary-context",
                    label: "Primary context",
                    icon: Compass,
                    node: (
                      <RefLink
                        resource="contexts"
                        id={artifact.primary_context_id}
                      />
                    ),
                  },
                ],
              },
            ]}
          />
        </InspectorSection>

        <InspectorSection title="Contexts">
          <ContextPanel artifactId={artifact.id} />
        </InspectorSection>

        <InspectorSection title="Timeline">
          <TaskTimeline artifactId={artifact.id} flush />
        </InspectorSection>

        {/* Provenance — the timeline's causal chain as a React Flow graph
            (architecture-map style, BLACK-only). Degrades inline; never throws. */}
        <InspectorSection title="Provenance">
          <ProvenanceGraph artifactId={artifact.id} height="40vh" />
        </InspectorSection>

        {/* Technical — ids / source / timestamps demoted to a muted tail (§E). */}
        <InspectorSection title="Technical">
          <FieldGrid className="text-muted-foreground">
            <Field label="Id">
              <TechnicalId id={artifact.id} />
            </Field>
            <Field label="Source system">{artifact.source_system}</Field>
            <Field label="Created">{formatDateTime(artifact.created_at)}</Field>
            <Field label="Updated">{formatDateTime(artifact.updated_at)}</Field>
          </FieldGrid>
        </InspectorSection>
      </Inspector>
      </SectionBoundary>

      <ClassifyDialog
        artifact={artifact}
        open={classifyOpen}
        onOpenChange={setClassifyOpen}
      />
    </>
  );
}

export default ArtifactShow;
